/**
 * Was this Google sign-in the one that CREATED the account, one that linked Google to an
 * existing email account, or an ordinary return visit? Analytics needs the first to count
 * Google signups, and must not count the second as one: a linked account is an existing
 * person, not a new one.
 *
 * Cognito gives the client no flag for this, but the ID token carries an `identities`
 * claim whose Google entry has the `dateCreated` of that identity: the moment the account
 * was created or the link was made. A first sign-in is one whose token was issued within
 * minutes of it. Both timestamps come from Cognito, so a wrong clock on the visitor's
 * machine cannot change the answer.
 *
 * Telling new from linked: a Google-only account is named "Google_<subject>" (the
 * federated username), while a linked account keeps its original email-account username.
 *
 * Misses fail towards "returning", i.e. the event the site sent before this existed. A
 * person who needed more than the window between creating the identity and finishing
 * sign-in (a first attempt that errored, retried later) is not counted as a signup.
 */
export type GoogleSignInKind = "new_account" | "linked_account" | "returning";

const FRESH_WINDOW_MS = 10 * 60 * 1000;
/** The identity can be stamped a moment after the token's own clock reads. */
const CLOCK_JITTER_MS = 60 * 1000;
const GOOGLE_USERNAME_PREFIX = "google_";

function claimsOf(idToken: string): Record<string, unknown> | null {
  try {
    const part = idToken.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const parsed: unknown = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
    return parsed !== null && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** `identities` is a JSON array in the token and a JSON string as a user attribute. */
function googleIdentityCreatedMs(raw: unknown): number | null {
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(list)) return null;
  for (const entry of list) {
    if (entry === null || typeof entry !== "object") continue;
    const { providerName, dateCreated } = entry as { providerName?: unknown; dateCreated?: unknown };
    if (String(providerName).toLowerCase() !== "google") continue;
    const created = Number(dateCreated);
    return Number.isFinite(created) ? created : null;
  }
  return null;
}

export function classifyGoogleSignIn(idToken: string): GoogleSignInKind {
  const claims = claimsOf(idToken);
  if (!claims) return "returning";
  const created = googleIdentityCreatedMs(claims.identities);
  const issuedS = Number(claims.iat ?? claims.auth_time);
  if (created === null || !Number.isFinite(issuedS)) return "returning";

  const sinceCreatedMs = issuedS * 1000 - created;
  if (sinceCreatedMs < -CLOCK_JITTER_MS || sinceCreatedMs > FRESH_WINDOW_MS) return "returning";

  const username = String(claims["cognito:username"] ?? "").toLowerCase();
  return username.startsWith(GOOGLE_USERNAME_PREFIX) ? "new_account" : "linked_account";
}
